import { useMemo, useState } from "react";
import {
  Bot,
  CirclePlay,
  Flag,
  GitMerge,
  Network,
  Play,
  Search,
  ShieldCheck,
} from "lucide-react";
import type { NodeKind, RunRecord } from "../../shared/types";

export interface NodeTemplate {
  kind: NodeKind;
  label: string;
  description: string;
}

const templates: Array<NodeTemplate & { category: string; icon: typeof Bot }> = [
  { kind: "trigger", label: "Gatilho", description: "Inicia por ação ou webhook", category: "Gatilhos", icon: Play },
  { kind: "orchestrator", label: "Orquestrador", description: "Planeja e distribui tarefas", category: "Agentes", icon: Network },
  { kind: "agent", label: "Agente", description: "Executa uma especialidade", category: "Agentes", icon: Bot },
  { kind: "merge", label: "Consolidar", description: "Aguarda e combina ramos", category: "Lógica", icon: GitMerge },
  { kind: "approval", label: "Aprovação", description: "Pausa para decisão humana", category: "Humano", icon: ShieldCheck },
  { kind: "output", label: "Saída", description: "Entrega o resultado final", category: "Saídas", icon: Flag },
];

interface NodePaletteProps {
  onAdd: (template: NodeTemplate) => void;
  recentRuns: RunRecord[];
  open?: boolean;
}

function formatCost(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

export function NodePalette({ onAdd, recentRuns, open = false }: NodePaletteProps) {
  const [tab, setTab] = useState<"nodes" | "runs">("nodes");
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return templates;
    return templates.filter((item) =>
      `${item.label} ${item.description} ${item.category}`.toLowerCase().includes(normalized),
    );
  }, [search]);
  const categories = Array.from(new Set(filtered.map((item) => item.category)));

  return (
    <aside className={`node-palette ${open ? "is-open" : ""}`}>
      <div className="segmented-control" aria-label="Conteúdo da barra lateral">
        <button className={tab === "nodes" ? "is-active" : ""} onClick={() => setTab("nodes")}>Nós</button>
        <button className={tab === "runs" ? "is-active" : ""} onClick={() => setTab("runs")}>Execuções</button>
      </div>

      {tab === "nodes" ? (
        <>
          <label className="search-field">
            <Search size={15} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar nós"
              aria-label="Buscar nós"
            />
          </label>
          <div className="palette-scroll">
            {categories.map((category) => (
              <section className="palette-group" key={category}>
                <h3>{category}</h3>
                {filtered.filter((item) => item.category === category).map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      className="palette-item"
                      key={`${item.category}-${item.kind}`}
                      onClick={() => onAdd(item)}
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.setData("application/go-win-node", JSON.stringify(item));
                        event.dataTransfer.effectAllowed = "move";
                      }}
                    >
                      <span className={`palette-item__icon palette-item__icon--${item.kind}`}><Icon size={15} /></span>
                      <span><strong>{item.label}</strong><small>{item.description}</small></span>
                    </button>
                  );
                })}
              </section>
            ))}
            {filtered.length === 0 && <p className="sidebar-empty">Nenhum nó encontrado.</p>}
          </div>
        </>
      ) : (
        <div className="palette-scroll run-list-mini">
          <div className="palette-section-title"><CirclePlay size={14} />Últimas execuções</div>
          {recentRuns.slice(0, 12).map((run) => (
            <div className="run-mini" key={run.id}>
              <span className={`run-state run-state--${run.status}`} />
              <div><strong>{run.workflowName ?? "Workflow"}</strong><small>{run.status}</small></div>
              <span>{formatCost(run.cost)}</span>
            </div>
          ))}
          {recentRuns.length === 0 && <p className="sidebar-empty">As execuções aparecerão aqui.</p>}
        </div>
      )}
    </aside>
  );
}
